import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import {
  CreateMemoryGameSelectGamesTemplate,
  SelectGamesPath,
} from '../../components/Templates';
import {
  StationMiniChallengeItemNo,
  StationSuperChallengeItemNo,
  StationType,
} from '../../constants/Games/gameStation';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

interface State {
  createType: QuickCreateGameMode | undefined;
  isNextDisabled: boolean;
}

const CreateMemoryGameSelectGamesScreen: React.FC = () => {
  const navigate = useNavigate();

  const [state, setState] = useState<State>({
    createType: undefined,
    isNextDisabled: false,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  const { mode } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    return {
      mode: _mode,
    };
  }, shallow);

  const hasAssistedGame = useDraftStore((store) => {
    return store.getHasAssistedGame();
  }, shallow);

  const gameParts = useDraftStore((store) => {
    return store.getGameParts();
  }, arrayEqual(shallow));

  const stationDrafts = useDraftStore((store) => {
    return store.getStationDrafts();
  }, arrayEqual(shallow));

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  const onSwitchGamifiedTutorial = useCallback(() => {
    useDraftStore.getState().setHasAssistedGame(!hasAssistedGame);
  }, [hasAssistedGame]);

  const onClickNext = useCallback(() => {
    // if (!state.isNextDisabled) {
    //   setState((s) => ({ ...s, showPopup: ShowPopupState.CreateJoruney }));
    // }
    navigate(-1);
  }, [navigate]);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const pathStations = useMemo(() => {
    const paths: SelectGamesPath[] = [];

    gameParts.forEach((part) => {
      const stations = stationDrafts.filter(
        (station) => station.partId === part.partId && !station.hidden,
      );
      paths.push({
        gameType: part.iconTag,
        isSelected: part.isSelected,
        stations: stations,
        partId: part.partId,
      });
    });

    return paths;
  }, [gameParts, stationDrafts]);

  const miniChallenges = useMemo(() => {
    const totalGameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game &&
        station.isSelected &&
        station.isForChallenge &&
        !station.hidden,
    ).length;
    const challengeCount = Math.max(0, Math.floor((totalGameCount - 2) / 3));
    return Array.from({ length: challengeCount }).map((_, index) => ({
      label: `${3 * (hasAssistedGame ? 2 : 1) * (index + 1) + 1}`, // TODO: need to recalculate for isForChallenge
      station: StationMiniChallengeItemNo.s0,
    }));
  }, [hasAssistedGame, stationDrafts]);

  const superChallenge = useMemo(() => {
    const totalGameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game &&
        station.isSelected &&
        station.isForChallenge &&
        !station.hidden,
    ).length;
    if (totalGameCount > 0) {
      const totalStationCount = stationDrafts.filter(
        (station) => station.isSelected && !station.hidden,
      ).length;
      return {
        label: `${totalStationCount + miniChallenges.length + 2}`,
        station: StationSuperChallengeItemNo.s0,
      };
    }
    return undefined;
  }, [miniChallenges.length, stationDrafts]);

  const onClickSelectGameType = useCallback(
    (partId: string, checked: boolean) => {
      useDraftStore.getState().changeSelectedGameType(partId, checked);
    },
    [],
  );

  const onClickSelectGame = useCallback(
    (partId: string, gameIndex: number, checked: boolean) => {
      useDraftStore.getState().changeSelectedGame(partId, gameIndex, checked);
    },
    [],
  );

  const albumFee = useMemo(() => {
    const gameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game &&
        station.isSelected &&
        !station.hidden,
    ).length;

    const challengeGameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game &&
        station.isSelected &&
        station.isForChallenge &&
        !station.hidden,
    ).length;

    // difficult quick memory game cost 2 stamina
    const gameFee =
      gameCount +
      (mode === QuickCreateGameMode.QuickMemory ? challengeGameCount : 0);

    const challengeFee = 0;
    // Math.max(0, Math.floor((challengeGameCount - 2) / 3)) +
    // (challengeGameCount > 0 ? 1 : 0);

    return gameFee + challengeFee;
  }, [mode, stationDrafts]);

  useEffect(() => {
    const hasSelected = stationDrafts.some((station) => station.isSelected);
    if (hasSelected) {
      setState((s) => ({ ...s, isNextDisabled: false }));
    } else {
      setState((s) => ({ ...s, isNextDisabled: true }));
    }
  }, [stationDrafts]);

  return (
    <>
      <CreateMemoryGameSelectGamesTemplate
        type={state.createType}
        webBoosterType={userAccount?.settings.webBoosterType}
        isGamifiedTutorials={hasAssistedGame}
        path={pathStations}
        miniChallenges={miniChallenges}
        superChallenge={superChallenge}
        isNextDisabled={state.isNextDisabled}
        expectedPayment={albumFee}
        onSwitchGamifiedTutorial={onSwitchGamifiedTutorial}
        onClickSelectGameType={onClickSelectGameType}
        onClickSelectGame={onClickSelectGame}
        onClickBack={onClickBack}
        onClickNext={onClickNext}
      />
    </>
  );
};
export default CreateMemoryGameSelectGamesScreen;
